import CryptoJS from 'crypto-js';
import { FaTwitter } from 'react-icons/fa';
import { FacebookIcon } from '../component/iconComponents/FacebookIcon';
import { KickIcon } from '../component/iconComponents/KickIcon';
import { TikTokIcon } from '../component/iconComponents/TikTokIcon';
import { TwitchIcon } from '../component/iconComponents/TwitchIcon';
import { YoutubeIcon } from '../component/iconComponents/YoutubeIcon';
import config from '../config';
import {
    getPublicProfileFromCache,
    getPublicProfilesFromCache,
    savePublicProfilesToCache,
    savePublicProfileToCache,
} from './cache';
import { GameInfo, getRoomsByUserFilter } from './lobby';
import { indexBy, PaginatedData } from './util';

export const SocialMediaPlatformMetadata = Object.freeze({
    twitch: { displayName: 'twitch', linkPrefix: 'https://twitch.tv/', icon: TwitchIcon() },
    kick: { displayName: 'kick', linkPrefix: 'https://kick.com/', icon: KickIcon() },
    google: { displayName: 'Youtube', linkPrefix: 'https://youtube.com/', icon: YoutubeIcon() },
    twitter: { displayName: 'twitter', linkPrefix: 'https://twitter.com/', icon: FaTwitter({}) },
    facebook: {
        displayName: 'facebook',
        linkPrefix: 'https://facebook.com/',
        icon: FacebookIcon(),
    },
    tiktok: { displayName: 'tiktok', linkPrefix: 'https://tiktok.com/', icon: TikTokIcon() },
});

const PROFILE_CACHE_EXPIRY_IN_MILLI = 60 * 60 * 1000;

export type SocialMediaLinkData = {
    handle: string;
    is_public: boolean;
};
export type SocialMediaLinkDataKey = keyof SocialMediaLinkData;

export type SocialMediaPlatformName = keyof typeof SocialMediaPlatformMetadata;
export type SocialMediaLinkDataMap = {
    [key in keyof typeof SocialMediaPlatformMetadata]?: SocialMediaLinkData;
};
export declare class TwitchUser {
    broadcaster_type: string;
    description: string;
    display_name: string;
    email?: string;
    id: string;
    login: string;
    offline_image_url: string;
    profile_image_url: string;
    type: string;
}

export declare class TwitchStream {
    id: string;
    user_id: string;
    user_login: string;
    user_name: string;
    game_id: string;
    game_name: string;
    type: string;
    title: string;
    // tags: string[]; // This currently isn't returned by social service
    viewer_count: number;
    started_at: string;
    language: string;
    thumbnail_url: string;
    is_mature: boolean;
}

export interface UserProfile {
    id: string;
    username: string;
    about_url: string;
    store_url: string;
    email?: string;
    description: string;
    avatar_object_url: string;
    social_media_links: SocialMediaLinkDataMap;
    twitch_profile?: TwitchUser;
    twitch_stream?: TwitchStream;
    retrieval_time_ms: number;
    has_stripe_connect_account: boolean;
}

export interface UserPrivateProfile extends UserProfile {
    first_name: string;
    last_name: string;
    email: string;
    receive_emails: boolean; //this is not currently being used on front end. There are currently 3 options instead: news & product updates, weekly newsletter, promotions and exclusive offers
    display_email_publicly: boolean;
    has_account: boolean;
}

export interface UserProfileUpdate {
    username: string;
    description: string;
    receive_emails: boolean;
    display_email_publicly: boolean;
    first_name: string;
    last_name: string;
    store_url: string;
    about_url: string;
}

export type UserSubscribedTo = {
    TopicID: string;
    UserID: string;
};

export type PresignedAvatarUploadData = {
    secret: string;
    presign: {
        url: string;
        method: string;
        signed_headers: {
            [key: string]: string;
        };
    };
};

export interface PublicProfileResponseWithPagination {
    profiles: { [key: string]: UserProfile };
    page: PaginatedData;
}

// More information can be found here: https://api-docs.igdb.com/#game
export interface Game {
    age_ratings: number[];
    aggregrated_rating: number;
    aggregrated_rating_count: number;
    alternative_names: number[];
    artworks: number[];
    bundles: number[];
    category: number;
    checksum: string;
    collection: number;
    cover: Cover;
    created_at: number;
    dlcs: number[];
    expanded_games: number[];
    expansions: number[];
    external_games: number[];
    first_release_date: number;
    follows: number;
    forks: number[];
    franchise: number;
    franchises: number[];
    game_engines: number[];
    game_localizations: number[];
    game_modes: number[];
    genres: number[];
    hypes: number;
    id: number; //this is not in docs but exists.
    involved_companies: number[];
    keywords: number[];
    language_supports: number[];
    multiplayer_modes: number[];
    name: string;
    parent_game: string;
    platforms: number[];
    player_perspectives: number[];
    ports: number[];
    rating: number;
    rating_count: number;
    release_dates: number[];
    remakes: number[];
    remasters: number[];
    screenshots: number[];
    similar_games: number[];
    slug: string;
    standalone_expansions: number[];
    status: number;
    storyline: string;
    summary: string;
    tags: number[];
    themes: number[];
    total_rating: number;
    total_rating_count: number;
    updated_at: number;
    url: string;
    version_parent: number;
    version_title: string;
    videos: number[];
    websites: Website[];
}

// More information can be found here: https://api-docs.igdb.com/#cover
export interface Cover {
    alpha_channel: boolean;
    animated: boolean;
    checksum: string;
    game: number;
    game_localization: number;
    height: number;
    image_id: number;
    // Note to access different image resolutions replace 't_thumb' in the url with one of the values listed here:
    // https://api-docs.igdb.com/#images
    // For example url.replace('t_thumb', 't_screenshot_big')
    url: string;
    width: number;
}

// More information can be found here: https://api-docs.igdb.com/#website
export interface Website {
    category: number;
    checksum: string;
    game: number;
    trusted: boolean;
    url: string;
}

// AccountLink is the resource representing an account link.
// For more details see https://stripe.com/docs/api/#account_links.
export interface StripeAccountLink {
    created: number;
    expires_at: number;
    object: string;
    url: string;
}

// Account is the resource representing your Stripe account.
// For more details see https://stripe.com/docs/api/#account.
export interface StripeAccount {
    id: string;
    requirements?: StripeAccountRequirements;
}

// Requirements for stripe account setup
export interface StripeAccountRequirements {
    disabled_reason?: string;
    currenty_due?: string[];
    past_due?: string[];
}

// LoginLink is the resource representing a login link for Express accounts.
// For more details see https://stripe.com/docs/api#login_link_object
export interface StripeLoginLink {
    created: number;
    url: string;
}

export const getFollowStatus = async (userID: string, loggedInUserID: string, token: string) => {
    const resp = await fetch(
        `${config.socialServiceURL}/user/${userID}/followed?query_user_id=${loggedInUserID}`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            method: 'GET',
        },
    );

    if (resp.status !== 200) {
        console.error(await resp.text());
        return false;
    }

    const data = await resp.json();

    return data.data;
};

export const getPublicProfiles = async (ids: string[]): Promise<{ [key: string]: UserProfile }> => {
    if (ids.length === 0) {
        return {};
    }

    let { uncachedIDs, cachedProfiles } = await splitProfilesByCached(ids);
    const queryString: ['id', string][] = uncachedIDs.map((request) => ['id', request]);
    const profiles = (await doPublicProfilesRequest(queryString)).map((profile) => {
        return { ...profile, retrieval_time_ms: Date.now() };
    });

    await savePublicProfilesToCache(profiles);

    const profileMap = indexBy(profiles, (profile) => profile.id);
    const cachedProfileMap = indexBy(cachedProfiles, (profile) => profile.id);

    return { ...cachedProfileMap, ...profileMap };
};

/**
 * NOTE: This does not do any caching and must be done by the caller
 * @param idQueries the ids of the user's to get the STREAMSIX profile's from
 * @returns The profiles if the id exists
 */
const doPublicProfilesRequest = async (
    idQueries: ['twitch_id' | 'id', string][],
): Promise<UserProfile[]> => {
    const MAX_PER_REQUEST = 200;

    if (!idQueries?.length) {
        return [];
    }

    let recursiveProfilesPromise: Promise<UserProfile[]> = Promise.resolve([]);
    if (idQueries.length > MAX_PER_REQUEST) {
        recursiveProfilesPromise = doPublicProfilesRequest(idQueries.slice(MAX_PER_REQUEST + 1));
        idQueries = idQueries.slice(0, MAX_PER_REQUEST + 1);
    }

    const resp = await fetch(
        `${config.socialServiceURL}/users/profiles/public?${new URLSearchParams(idQueries)}`,
        {
            headers: {
                'Content-Type': 'application/json',
            },
            method: 'GET',
        },
    );

    if (resp.status !== 200) {
        console.error(await resp.text());
        throw new Error('failed to retrieve users public profile');
    }

    const jsonResp: { type: 'success'; data: UserProfile[] } = await resp.json();

    const recursiveProfiles = await recursiveProfilesPromise;

    return [...jsonResp.data, ...recursiveProfiles];
};

export const getPublicProfilesByTwitchID = async (
    twitchIDs: string[],
): Promise<{ [twitchID: string]: UserProfile }> => {
    if (twitchIDs.length === 0) {
        return {};
    }

    const { uncachedIDs, cachedProfiles } = await splitProfilesByCached(twitchIDs);
    const query: ['twitch_id', string][] = uncachedIDs.map((id) => ['twitch_id', id]);

    const profiles = await doPublicProfilesRequest(query);

    // Because we retrieve the profiles by the associated twitch ID,
    // they necessarily have a defined twitch_profile
    const profileMap = indexBy(profiles, (profile) => profile.twitch_profile!.id);
    const cachedProfileMap = indexBy(cachedProfiles, (profile) => profile.twitch_profile!.id);

    return { ...cachedProfileMap, ...profileMap };
};

export const getPublicProfilesByUsername = async (
    username: string,
    isExact: boolean,
    page: string,
    signal?: AbortSignal,
): Promise<PublicProfileResponseWithPagination> => {
    if (username.length < 3) {
        throw new Error('expect at least 3 characters in username');
    }

    let query = '';
    if (page === '') {
        const queryString = new URLSearchParams([
            ['username', username],
            ['exact', isExact ? 'true' : 'false'],
        ]);
        query = `${queryString}`;
    } else {
        query = page;
    }

    const resp = await fetch(`${config.socialServiceURL}/users/profiles/public?${query}`, {
        headers: {
            'Content-Type': 'application/json',
        },
        method: 'GET',
        signal: signal,
    });

    if (resp.status !== 200) {
        console.error(await resp.text());
        throw new Error('failed to retrieve users public profile');
    }

    const data: { type: string; data: UserProfile[]; page: PaginatedData } = await resp.json();
    data.data.forEach((x) => {
        x.retrieval_time_ms = Date.now();
    });
    const profiles = data.data.reduce((acc, profile) => {
        return { ...acc, [profile.id]: profile };
    }, {});

    return { profiles, page: data.page };
};

export const getUserPublicProfile = async (
    userID: string,
    skipCache?: boolean,
): Promise<UserProfile> => {
    const savedData = await getPublicProfileFromCache(userID);
    if (savedData && !skipCache) {
        if (
            savedData.id === userID &&
            savedData.retrieval_time_ms > Date.now() - PROFILE_CACHE_EXPIRY_IN_MILLI // cache expires after 1 hour
        ) {
            return savedData;
        }
    }

    const resp = await fetch(`${config.socialServiceURL}/user/${userID}/profile/public`, {
        headers: {
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        console.error(await resp.text());
        throw new Error('failed to retrieve users public profile');
    }

    const data: { type: 'success'; data: UserProfile } = await resp.json();
    savePublicProfileToCache(data.data);
    return data.data;
};

export const getUserPrivateProfile = async (
    userID: string,
    token: string,
): Promise<UserPrivateProfile> => {
    const resp = await fetch(`${config.socialServiceURL}/user/${userID}/profile/private`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        console.error(await resp.text());
        throw new Error('failed to retrieve users private profile');
    }

    const data = await resp.json();

    return data.data;
};

export const setUserPrivateProfile = async (
    userID: string,
    token: string,
    profile: UserProfileUpdate,
): Promise<Response> => {
    const resp = await fetch(`${config.socialServiceURL}/user/${userID}/profile`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(profile),
        method: 'PUT',
    });
    return resp;
};

const calculateFileMD5 = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsArrayBuffer(file || new Blob());
        reader.onload = () => {
            const buffer = reader.result as ArrayBuffer;
            // @ts-ignore
            const wordArray = CryptoJS.lib.WordArray.create(buffer);
            const hash = CryptoJS.MD5(wordArray).toString();
            resolve(hash);
        };
    });
};

export const getPresignedAvatarUploadData = async (
    userID: string,
    token: string,
    file: File,
): Promise<PresignedAvatarUploadData> => {
    const md5sum = await calculateFileMD5(file);
    const resp = await fetch(`${config.socialServiceURL}/user/${userID}/profile/avatar`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            filename: file.name,
            content_md5: md5sum,
            content_length: file.size,
            // 30 minutes for the user to decide to click "save" after selecting their avatar is plenty
            timeout_seconds: 1800,
        }),
        method: 'POST',
    });
    if (resp.status !== 200) {
        console.error(resp);
        throw new Error("Hmmm something didn't go right...please try again later");
    } else {
        const response = await resp.json();
        return response.data;
    }
};

export const uploadPresignedAvatar = async (
    userID: string,
    token: string,
    presignedAvatarUploadData: PresignedAvatarUploadData,
    file: File,
): Promise<void> => {
    console.log('Avatar Host: ', presignedAvatarUploadData.presign.url);
    console.log('Avatar Method: ', presignedAvatarUploadData.presign.method);
    console.log(
        'Avatar Headers: ',
        JSON.stringify(presignedAvatarUploadData.presign.signed_headers),
    );

    const avatarUploadResp = await fetch(presignedAvatarUploadData.presign.url, {
        method: presignedAvatarUploadData.presign.method,
        headers: presignedAvatarUploadData.presign.signed_headers,
        body: file,
    });

    if (!avatarUploadResp.ok) {
        console.error(avatarUploadResp);
        throw new Error('failed to upload avatar');
    }

    const confirmResp = await fetch(
        `${config.socialServiceURL}/user/${userID}/profile/avatar_confirm`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ secret: presignedAvatarUploadData.secret }),
            method: 'POST',
        },
    );

    if (!confirmResp.ok) {
        console.error(confirmResp);
        throw new Error('failed to confirm avatar upload');
    }
};

export const uploadAvatar = async (userID: string, token: string, file: File): Promise<boolean> => {
    try {
        const presignedData = await getPresignedAvatarUploadData(userID, token, file);
        await uploadPresignedAvatar(userID, token, presignedData, file);
        return true;
    } catch {
        return false;
    }
};

export const deleteAvatar = async (userID: string, token: string): Promise<boolean> => {
    const resp = await fetch(`${config.socialServiceURL}/user/${userID}/profile/avatar`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
        method: 'DELETE',
    });

    if (!resp.ok) {
        console.error(await resp.text());
        return false;
    }

    console.log('Deleted avatar');
    return true;
};

export const getSocialMediaLinks = async (
    userID: string,
    token: string,
): Promise<SocialMediaLinkDataMap> => {
    const resp = await fetch(`${config.socialServiceURL}/user/${userID}/profile/links`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        console.error(await resp.text());
        throw new Error('failed to retrieve social media links');
    }

    const data = await resp.json();
    return data.data;
};

export const setSocialMediaLinks = async (
    userID: string,
    token: string,
    socialLinkData: SocialMediaLinkDataMap,
): Promise<SocialMediaLinkDataMap> => {
    const resp = await fetch(`${config.socialServiceURL}/user/${userID}/profile/links`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({ linked_properties: socialLinkData }),
    });

    if (resp.status !== 200) {
        console.error(await resp.text());
        throw new Error('failed to set social media links');
    }

    const data = await resp.json();
    return data.data;
};

export const getUserFollowingCount = async (userID: string, token: string) => {
    const resp = await fetch(`${config.socialServiceURL}/user/${userID}/following/count`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        console.error(await resp.text());
        return null;
    }

    const data = await resp.json();

    return data.data;
};

export const getUserFollowing = async (userID: string, token: string): Promise<UserProfile[]> => {
    const resp = await fetch(`${config.socialServiceURL}/user/${userID}/following`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        console.error(await resp.text());
        throw new Error('failed to retrieve users following list');
    }

    const data = await resp.json();

    return data.data;
};

export const getUserSubscribedTo = async (
    userID: string,
    token: string,
): Promise<UserSubscribedTo[]> => {
    const resp = await fetch(`${config.socialServiceURL}/user/${userID}/notifications/subscribed`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        console.error(await resp.text());
        throw new Error('failed to retrieve subscribed topic list');
    }

    const data = await resp.json();

    return data.data;
};

export const getUserFollowersCount = async (userID: string) => {
    const resp = await fetch(`${config.socialServiceURL}/user/${userID}/followers/count`, {
        headers: {
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        console.error(await resp.text());
        return null;
    }

    const data = await resp.json();

    return data.data;
};

export const getUserFollowers = async (userID: string, token: string): Promise<UserProfile[]> => {
    const resp = await fetch(`${config.socialServiceURL}/user/${userID}/followers`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        console.error(await resp.text());
        throw new Error('failed to retrieve users followers list');
    }

    const data = await resp.json();

    return data.data;
};

export const checkIsFollowing = async (
    userID: string,
    targetUserID: string,
    token: string,
): Promise<boolean | undefined> => {
    const resp = await fetch(
        `${config.socialServiceURL}/user/${userID}/following/${targetUserID}`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            method: 'GET',
        },
    );

    if (resp.status !== 200) {
        console.error(await resp.text());
        return undefined;
    }

    const data = await resp.json();

    return data.data;
};

export const followUser = async (userID: string, targetUserID: string, token: string) => {
    const resp = await fetch(`${config.socialServiceURL}/user/${userID}/following`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({
            target_user_id: targetUserID,
        }),
    });

    if (resp.status !== 200) {
        console.error(await resp.text());
        return null;
    }

    const data = await resp.json();

    console.log('followUser', data);

    return data.type === 'success';
};

export const unfollowUser = async (userID: string, targetUserID: string, token: string) => {
    const resp = await fetch(
        `${config.socialServiceURL}/user/${userID}/following/${targetUserID}`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: 'DELETE',
        },
    );

    if (resp.status !== 200) {
        console.error(await resp.text());
        return null;
    }

    const data = await resp.json();

    console.log('unfollowUser', data);

    return data.type === 'success';
};

export const setupNotificationUser = async (
    token: string | undefined,
    firebaseToken: string,
    userID: string | undefined,
) => {
    if (userID === undefined || token === undefined) {
        return;
    }

    const resp = await fetch(`${config.socialServiceURL}/user/${userID}/notifications/setup`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            token: firebaseToken,
        }),
        method: 'POST',
    });

    if (resp.status !== 200) {
        console.error(await resp.text());
        return null;
    }

    const data = await resp.json();
    return data.data;
};

export const isSubscribedToNotifications = async (
    token: string,
    firebaseToken: string,
    userID: string,
    topic: string,
) => {
    if (userID === undefined || token === undefined || firebaseToken === undefined) {
        return;
    }

    const resp = await fetch(
        `${config.socialServiceURL}/user/${userID}/notifications/topics/subscribed`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                Token: firebaseToken,
                Topic: topic,
            }),
            method: 'POST',
        },
    );

    if (resp.status !== 200) {
        console.error(await resp.text());
        return null;
    }

    const data = await resp.json();

    return data.data;
};

export const subscribeToNotificationTopic = async (
    token: string,
    firebaseToken: string,
    userID: string,
    topic: string,
) => {
    if (userID === undefined || token === undefined || firebaseToken === undefined) {
        return null;
    }

    const resp = await fetch(
        `${config.socialServiceURL}/user/${userID}/notifications/topics/subscribe`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                Token: firebaseToken,
                Topic: topic,
            }),
            method: 'POST',
        },
    );

    if (resp.status !== 200) {
        console.error(await resp.text());
        return null;
    }

    const data = await resp.json();

    return data.data;
};

export const unsubscribeFromNotificationTopic = async (
    token: string,
    firebaseToken: string,
    userID: string,
    topic: string,
) => {
    if (userID === undefined || token === undefined || firebaseToken === undefined) {
        return null;
    }

    const resp = await fetch(
        `${config.socialServiceURL}/user/${userID}/notifications/topics/unsubscribe`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                Token: firebaseToken,
                Topic: topic,
            }),
            method: 'DELETE',
        },
    );

    if (resp.status !== 200) {
        console.error(await resp.text());
        return null;
    }

    const data = await resp.json();

    return data.data;
};

export const isLive = async (
    userProfile: UserProfile,
    gameInfo?: GameInfo,
): Promise<{ isUserLive: boolean; gameInfo?: GameInfo }> => {
    const liveTwitch = userProfile.twitch_stream?.type === 'live';
    if (!liveTwitch) {
        return { isUserLive: false };
    }

    let liveGameInfo = gameInfo;
    if (!liveGameInfo) {
        const liveGameInfoList = await getRoomsByUserFilter({
            userFilter: { streamsix: [userProfile.id] },
        });

        if (!liveGameInfoList || liveGameInfoList.length === 0) {
            return { isUserLive: false };
        }

        liveGameInfo = liveGameInfoList?.find((rmbInfo) => rmbInfo.owner === userProfile.id);
        if (liveGameInfo === undefined) {
            return { isUserLive: false };
        }
    }

    return { isUserLive: true, gameInfo };
};

export const getStreamsixIGDBGames = async (): Promise<Game[]> => {
    const resp = await fetch(`${config.socialServiceURL}/igdb/streamsix/games`, {
        headers: {
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        console.error(await resp.text());
        return [];
    }

    const data = await resp.json();

    console.log('getStreamsixGames', data);

    return data.data;
};

const splitProfilesByCached = async (
    ids: string[],
): Promise<{ uncachedIDs: string[]; cachedProfiles: UserProfile[] }> => {
    let cachedProfiles: UserProfile[] = [];
    let uncachedIDs: string[] = [];

    const loadedProfiles = await getPublicProfilesFromCache(ids);

    ids.forEach((id) => {
        const matchedProfile = loadedProfiles.find(
            (lp) =>
                id === lp.id && lp.retrieval_time_ms > Date.now() - PROFILE_CACHE_EXPIRY_IN_MILLI,
        );
        if (matchedProfile) {
            cachedProfiles.push(matchedProfile);
        } else {
            uncachedIDs.push(id);
        }
    });
    return { uncachedIDs, cachedProfiles };
};

export const createStripeAccount = async (
    userID: string,
    token: string,
    successUrl: string,
    failureUrl: string,
): Promise<StripeAccountLink | undefined> => {
    const resp = await fetch(`${config.socialServiceURL}/user/${userID}/stripe`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            success_url: successUrl,
            failure_url: failureUrl,
        }),
        method: 'PUT',
    });

    if (resp.status !== 200) {
        console.error(await resp.text());
        return undefined;
    }

    const data = await resp.json();

    console.log('createStripeAccount', data);

    return data.data;
};

export const getStripeAccount = async (
    userID: string,
    token: string,
): Promise<StripeAccount | undefined> => {
    const resp = await fetch(`${config.socialServiceURL}/user/${userID}/stripe`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        console.error(await resp.text());
        return undefined;
    }

    const data = await resp.json();

    console.log('getStripeAccount', data);

    return data.data;
};

export const getStripeDashboard = async (
    userID: string,
    token: string,
): Promise<StripeLoginLink | undefined> => {
    const resp = await fetch(`${config.socialServiceURL}/user/${userID}/stripe/dashboard`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        method: 'GET',
    });

    if (resp.status !== 200) {
        console.error(await resp.text());
        return undefined;
    }

    const data = await resp.json();

    console.log('getStripeDashboard', data);

    return data.data;
};
